import { type FunctionComponent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { ChatIcon } from '@instacart/ids-core'
import { notify, NotificationLevel } from '@retailer-platform/shared-components'
import {
  TDS_DEFAULT_COLORS,
  typography,
  useTheme,
} from '@retailer-platform/shared-components/src/tds'
import { useAdminControls } from '../../../utils/contexts/admin-controls/AdminControlsContext'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { Permission } from '../../../legacy/common/types'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { useNavV3OptOutSurvey } from '../../common/retailer-satisfaction-survey/nav-v3-opt-out/NavV3OptOutSurveyProvider'
import { getNavVersionSwitchedEventData } from './navV3.tracking'

const FEEDBACK_SLACK_URL = 'https://instacart.enterprise.slack.com/archives/C06917XU8TF'
const FEEDBACK_TOAST_ID = 'nav-v3-feedback'

// User-facing toggle between v2 (legacy MenuBarTop) and v3 (TdsNavigationBar).
// Backed by the same `useTdsNav` admin control that the Admin Controls modal
// switch writes — persisted in localStorage. Shown to all users while v2 is
// being decommissioned, so anyone hitting issues on v3 can flip back.
//
// When `target='v2'` is clicked (i.e. user is leaving v3), this also opens
// the NavV3OptOutSurvey via NavV3OptOutSurveyProvider. The modal opens on
// every switch so users see the June 30 permanence notice each time. The
// provider lives above the nav so the modal survives the v3→v2 unmount that
// this very click triggers.
//
// `target` is the version this button switches *to*: 'v3' for the opt-in
// rendered in the v2 top bar, 'v2' for the opt-out rendered in the v3 top bar.
//
// `appearance` picks the color treatment to match the surrounding nav. The
// retailer/IDP topbar is brand green, so the button uses brandPrimaryRegular.
// The admin topbar is dark gray (systemGrayscale80), where the green clashes,
// so the admin appearance uses a lighter gray that reads as a button without
// fighting the topbar's monochrome palette.
type Props = { target: 'v2' | 'v3'; appearance?: 'retailer' | 'admin' }

export const NavVersionSwitcherButton: FunctionComponent<Props> = ({
  target,
  appearance = 'retailer',
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const { setAdminControls } = useAdminControls()
  const hasAccess = useAccessControl()
  const isInternalAdmin = hasAccess({ permissions: Permission.AdminPanel })
  const { openSurvey } = useNavV3OptOutSurvey()
  const trackEvent = useTrackEvent()

  const labelId =
    target === 'v3' ? 'app.nav.versionSwitcher.tryNew' : 'app.nav.versionSwitcher.switchToClassic'

  const palette =
    appearance === 'admin'
      ? {
          background: theme.colors.systemGrayscale60,
          hover: theme.colors.systemGrayscale80,
          active: theme.colors.systemGrayscale80,
        }
      : {
          background: theme.colors.brandPrimaryRegular,
          hover: theme.colors.brandPrimaryDark,
          active: theme.colors.brandPrimaryExtraDark,
        }

  return (
    <button
      type="button"
      onClick={() => {
        // When opting out of v3, surface the feedback survey BEFORE flipping
        // the admin control. The survey lives in NavV3OptOutSurveyProvider
        // above the nav so it survives the v3→v2 unmount triggered by the
        // line below.
        if (target === 'v2') {
          openSurvey()
        }
        // Track BEFORE flipping the control so the event fires even when the
        // flip unmounts this component (v3→v2 case).
        const from = target === 'v3' ? 'v2' : 'v3'
        trackEvent(getNavVersionSwitchedEventData(from, target, 'switcherButton'))
        // Atomic write: setting useTdsNav and navV3OptOutAt in separate
        // setAdminControl calls would race (both close over the same stale
        // settings snapshot). On v3→v2 we stamp the opt-out time so the gate
        // can expire it after 24h; on v2→v3 we clear the stamp so the next
        // opt-out gets a fresh window.
        setAdminControls({
          useTdsNav: target === 'v3',
          navV3OptOutAt: target === 'v2' ? Date.now() : undefined,
        })
        if (isInternalAdmin) {
          notify({
            id: FEEDBACK_TOAST_ID,
            level: NotificationLevel.Info,
            timeout: 8000,
            icon: <ChatIcon fill={TDS_DEFAULT_COLORS.colors.brandPrimaryRegular} size={28} />,
            contents: intl.formatMessage(
              { id: 'app.nav.versionSwitcher.feedbackToast' },
              {
                link: (chunks: ReactNode) => (
                  <a href={FEEDBACK_SLACK_URL} target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              }
            ),
          })
        }
      }}
      css={{
        ...typography.accentEmphasized,
        height: 28,
        padding: '0 16px',
        borderRadius: 4,
        border: 'none',
        cursor: 'pointer',
        color: theme.colors.systemGrayscale00,
        backgroundColor: palette.background,
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: palette.hover },
        '&:active': { backgroundColor: palette.active },
        '&:focus-visible': {
          outline: `2px solid ${theme.colors.systemGrayscale00}`,
          outlineOffset: 2,
        },
      }}
    >
      {intl.formatMessage({ id: labelId })}
    </button>
  )
}
